import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import cx from 'classnames';
import './SelectClientDropdown.less';

import { useAuth } from '../../../../../hooks/useAuth';
import { ClientModel, TagModel } from '../../../../../models/client';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { fetchDropdownClients } from '../../../../../store/features/clientDropdown/clientDropdownSlice';
import {
  getCurrentUser,
  updateUserClient,
} from '../../../../../store/features/users/usersSlice';
import { UserPermissions } from '../../../../../util';

interface SelectClientDropdownProps {
  collapsed: boolean;
}

const SelectClientDropdown = ({ collapsed }: SelectClientDropdownProps) => {
  const dispatch = useAppDispatch();
  const {
    clients,
    loading: fetchingClients,
    pagination,
    hasMore,
  } = useAppSelector((state) => state.clientDropdown);
  const { currentUser } = useAppSelector((state) => state.users);
  const { hasPermission } = useAuth();
  const { fetchingUser } = useAppSelector((state) => state.users);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const clientDropdownClass = cx('select-client-dropdown', {
    collapsed: collapsed,
  });

  const clientDropdownOverlayClass = cx('client-dropdown-menu-overlay', {
    collapsed: collapsed,
  });

  const getClientName = useCallback((): string => {
    if (!clients.length || !currentUser) {
      return 'No Client';
    }

    const client = clients.find(
      (client) => client.uuid === currentUser.client_id
    );
    const clientName = client ? client.name : 'No Client';

    return collapsed && clientName ? clientName[0] : clientName;
  }, [clients, currentUser, collapsed]);

  const handleUpdateClient = useCallback(
    (clientId: string) => {
      if (!currentUser?.uuid) return;

      dispatch(
        updateUserClient({
          userId: currentUser.uuid,
          clientId,
        })
      ).then(() => {
        window.location.reload();
      });
      setIsOpen(false);
    },
    [dispatch, currentUser?.uuid]
  );

  const filteredClients = useMemo(() => {
    return clients
      .filter((item: ClientModel) => {
        return (
          item.tags &&
          item.tags.some(
            (tag: TagModel) => tag.name === 'Client' && tag.set === 1
          )
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      if (
        !fetchingClients &&
        hasMore &&
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 1 &&
        pagination
      ) {
        dispatch(
          fetchDropdownClients({ offset: pagination.offset + pagination.limit })
        );
      }
    },
    [pagination, dispatch, fetchingClients, hasMore]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const canSwitchClients = hasPermission(UserPermissions.UserSwitchClients);

    if (canSwitchClients) {
      dispatch(fetchDropdownClients({}));
      dispatch(getCurrentUser());
    }
  }, [dispatch, hasPermission]);

  if (!hasPermission(UserPermissions.UserSwitchClients)) {
    return null;
  }

  if (fetchingClients || fetchingUser) {
    return (
      <div className={clientDropdownClass}>
        <LoadingOutlined style={{ color: 'white' }} />
      </div>
    );
  }

  return (
    <div className="select-client-dropdown-wrapper" ref={dropdownRef}>
      <div className={clientDropdownClass} onClick={toggleDropdown}>
        <div className="client-name-text">{getClientName()}</div>
      </div>

      {isOpen && (
        <div className={`select-client-menu ${clientDropdownOverlayClass}`}>
          <div
            ref={menuRef}
            className="select-client-menu-scrollable"
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
            }}
            onScroll={handleScroll}
          >
            {filteredClients.map((client: ClientModel) => (
              <div
                key={client.uuid}
                className="select-client-menu-item"
                onClick={() => handleUpdateClient(client.uuid)}
              >
                {client.name}
              </div>
            ))}
            {fetchingClients && (
              <div className="select-client-menu-item">
                <LoadingOutlined style={{ color: 'white' }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectClientDropdown;
